import React, { useState, useRef } from 'react';
import '../style/form.scss';
import InputField from './InputField.tsx';
import { ReactComponent as IconProfile } from '../assets/icons/icon_m_profile.svg';
import { ReactComponent as IconPhone } from '../assets/icons/icon_m_phone.svg';
import { ReactComponent as IconMail } from '../assets/icons/icon_m_mail.svg';
import { Orbit } from '@uiball/loaders';

interface FormProps {}

const Form: React.FC<FormProps> = () => {
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [contact, setContact] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [contactError, setContactError] = useState<string>('');
  const [generalContactError, setGeneralContactError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formButtonRef = useRef<HTMLButtonElement>(null);

  const validateForm = () => {
    let isValid = true;
    setNameError('');
    setPhoneError('');
    setContactError('');
    setGeneralContactError('');

    if (!name.trim()) {
      setNameError('Укажите данные');
      isValid = false;
    }

    const rawPhone = phone.replace(/[^\d]/g, '');
    const isPhoneCompleted = rawPhone.length === 11;
    const isContactFilled = contact.trim() !== '';

    if (rawPhone.length > 1 && rawPhone.length < 11) {
      setPhoneError('Номер не полный');
      isValid = false;
    } else if (!isPhoneCompleted && !isContactFilled) {
      setGeneralContactError('Укажите телефон или контакт');
      isValid = false;
    }

    if (!isContactFilled && !isPhoneCompleted) {
      setContactError('Укажите контакт');
      isValid = false;
    }

    return isValid;
  };

  const resetForm = () => {
    setName('');
    setPhone('');
    setContact('');
    setNameError('');
    setPhoneError('');
    setContactError('');
    setGeneralContactError('');
    setIsSubmitted(false);
    setIsLoading(false);
    setErrorMessage('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isSubmitted) {
      resetForm();
      return;
    }

    const formIsValid = validateForm();

    if (formIsValid) {
      setIsLoading(true);
      try {
        const backendUrl = 'https://copylobby-email-backend.vercel.app/api/submit-form';
        const response = await fetch(backendUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name, phone, contact }),
        });

        if (response.ok) {
          console.log('Заявка успешно отправлена!');
          setIsSubmitted(true);
        } else {
          const errorData = await response.json();
          console.error('Ошибка при отправке заявки:', errorData.message);
          setErrorMessage(errorData.message || 'Произошла ошибка');
          setTimeout(() => {
            setErrorMessage('');
          }, 1500); // Ошибка отображается 3 секунды
        }
      } catch (error) {
        console.error('Произошла ошибка сети или сервера:', error);
        setErrorMessage('Ошибка сети или сервера');
        setTimeout(() => {
          setErrorMessage('');
        }, 1500); // Ошибка отображается 3 секунды
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log('Ошибка валидации формы. Проверьте поля.');
    }
  };

  return (
    <div className="form-block">
      <div className="form-background-image"></div>
      <form className="form" onSubmit={handleSubmit}>
        <div className="headline">
          <h4 className="h4">
            {isSubmitted ? 'Заявка отправлена!' : 'Обсудить проект'}
          </h4>
        </div>

        <div className="form-container">
          <div className="content-container">
            <div className={`inputs-wrapper ${isSubmitted ? 'hidden' : ''}`}>
              <div className="inputs-container">
                <InputField
                  label="Имя"
                  icon={IconProfile}
                  type="text"
                  placeholder="Введите имя"
                  value={name}
                  onChange={setName}
                  error={nameError}
                  required={true}
                />
                <InputField
                  label="Телефон"
                  icon={IconPhone}
                  type="tel"
                  placeholder="Введите номер"
                  value={phone}
                  onChange={setPhone}
                  error={phoneError}
                  required={true}
                  generalContactError={generalContactError}
                />
                <InputField
                  label="Почта"
                  icon={IconMail}
                  type="mail"
                  placeholder="Введите почту"
                  value={contact}
                  onChange={setContact}
                  error={contactError}
                  required={false}
                  generalContactError={generalContactError}
                />
              </div>
              <div className="caption body-s">Отправляя форму вы соглашаетесь с обработкой данных.</div>
            </div>
            <div className={`success-wrapper ${isSubmitted ? '' : 'hidden'}`}>
              <p className="body-m" style={{ textAlign: 'left', width: '100%' }}>
                Наш менеджер свяжется с&nbsp;вами в&nbsp;ближайшее время, чтобы обсудить детали проекта.
              </p>
            </div>
          </div>

          <button
            type="submit"
            className={`form-button ${errorMessage ? 'error' : ''}`}
            disabled={isLoading && !isSubmitted}
            ref={formButtonRef}
          >
            <div className="text-container">
              {isLoading && !isSubmitted ? (
                <Orbit size={30} speed={1.5} color="black" />
              ) : errorMessage ? (
                <span className="label body-m-medium">{errorMessage}</span>
              ) : (
                <span className="label body-m-medium">
                  {isSubmitted ? 'Подтвердить' : 'Отправить заявку'}
                </span>
              )}
            </div>
          </button>
        </div>
      </form>
    </div>
  );
};

export default Form;